import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/navbar';
import Footer from '../../components/footer';
import { ConfirmModal, DeliveryDateModal } from '../../components/box-modal';
import axios from 'axios';
import { FaShoppingCart, FaPlus, FaMinus, FaCheck, FaInfoCircle } from 'react-icons/fa';

const parsePrice = (priceString) => parseFloat(priceString.replace('$', ''));

const ProductCard = ({
  product,
  updateCartCount,
  forceUpdateKey,
  uniqueProductsInCart,
  over20ProductsCount,
  between10And20ProductsCount,
  hasIncompatibleItems,
  onCartConflictError,
}) => {
  const [inCart, setInCart] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    const foundItem = storedCartItems.find((item) => item.id === product.id);
    if (foundItem) {
      setInCart(true);
      setQuantity(foundItem.quantity);
    } else {
      setInCart(false);
      setQuantity(1);
    }
  }, [product.id, forceUpdateKey]);

  const handleAddToCart = () => {
    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    // Real-time check for incompatible items
    const hasIncompatible = storedCartItems.some(item => item.orderType !== "Large Box");
    if (hasIncompatible) {
      onCartConflictError(); // Notify parent component
      return;
    }

    if (!inCart) {
      const productPrice = parsePrice(product.price);
      const newOver20Count = productPrice > 20 ? over20ProductsCount + 1 : over20ProductsCount;
      const newBetween10And20Count = (productPrice >= 10 && productPrice <= 20) ? between10And20ProductsCount + 1 : between10And20ProductsCount;

      if (productPrice > 20 && newOver20Count > 1) {
        return; // Do not add if it exceeds the limit for products over $20
      }

      if (productPrice >= 10 && productPrice <= 20 && newBetween10And20Count > 2) {
        return; // Do not add if it exceeds the limit for products between $10 and $20
      }

      setInCart(true);
      saveToLocalStorage(product, quantity);
      updateCartCount();
    }
  };

  const removeFromCart = () => {
    const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    const updatedCart = cartItems.filter(item => item.id !== product.id);
    localStorage.setItem('cartItems', JSON.stringify(updatedCart));
    setInCart(false);
    setQuantity(1);
    updateCartCount();
  };

  const saveToLocalStorage = (product, quantity) => {
    const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    const updatedCart = [
      ...cartItems,
      { ...product, quantity, orderType: 'Large Box' }
    ];
    localStorage.setItem('cartItems', JSON.stringify(updatedCart));
  };

  const productPrice = parsePrice(product.price);
  const isOver20ProductDisabled = productPrice > 20 && over20ProductsCount >= 1;
  const isBetween10And20ProductDisabled = productPrice >= 10 && productPrice <= 20 && between10And20ProductsCount >= 2;

  const getButtonText = () => {
    if (hasIncompatibleItems) return 'Cart Conflict - Clear First';
    if (inCart) return 'Product Added';
    if (uniqueProductsInCart >= 10) return 'Limit reached';
    if (isOver20ProductDisabled) return 'Limit reached for $20+ product';
    if (isBetween10And20ProductDisabled) return 'Limit reached for $10-$20 product';
    return 'Add to Box';
  };

  return (
    <div className="bg-white rounded-lg overflow-hidden shadow-lg transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1">
      <div className="relative">
        <img 
          src={product.img} 
          alt={product.name} 
          className="h-64 w-full object-cover"
        />
        {(productPrice > 20) && (
          <span className="absolute top-0 right-0 bg-orange-500 text-white px-2 py-1 text-xs font-bold">
            Premium
          </span>
        )}
        <button 
          onClick={() => setShowDetails(!showDetails)}
          className="absolute bottom-2 right-2 bg-white bg-opacity-80 rounded-full p-2 text-gray-700 hover:bg-opacity-100"
        >
          <FaInfoCircle size={18} />
        </button>
      </div>
      
      <div className="p-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold text-gray-800 line-clamp-2">{product.name}</h3>
            <p className="text-sm text-green-700 font-medium">{product.brand}</p>
          </div>
          <p className="text-lg font-bold text-green-800">${product.price}</p>
        </div>
        
        {showDetails && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <p className="text-sm text-gray-600">{product.description || "No description available."}</p>
            <p className="text-xs text-gray-500 mt-1">Category: {product.category || "Uncategorized"}</p>
          </div>
        )}

        <div className="mt-4 flex justify-between items-center">
          {inCart ? (
            <div className="flex items-center justify-between w-full">
              <button
                className="flex items-center justify-center space-x-2 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg cursor-not-allowed"
                disabled
              >
                <FaCheck size={16} />
                <span>Product Added</span>
              </button>
              <button 
                onClick={removeFromCart}
                className="text-red-500 text-sm hover:text-red-700"
              >
                Remove
              </button>
            </div>
          ) : (
            <button
              onClick={handleAddToCart}
              className={`flex items-center justify-center space-x-2 bg-green-600 text-white py-2 px-4 rounded-lg transition-colors ${
                uniqueProductsInCart >= 10 || isOver20ProductDisabled || isBetween10And20ProductDisabled || hasIncompatibleItems
                  ? 'opacity-50 cursor-not-allowed bg-gray-400'
                  : 'hover:bg-green-700'
              }`}
              disabled={uniqueProductsInCart >= 10 || isOver20ProductDisabled || isBetween10And20ProductDisabled || hasIncompatibleItems}
            >
              {inCart ? <FaCheck size={16} /> : <FaShoppingCart size={16} />}
              <span>{getButtonText()}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const LargeBoxPage = () => {
  const [cartCount, setCartCount] = useState(0);
  const [uniqueProductsInCart, setUniqueProductsInCart] = useState(0);
  const [over20ProductsCount, setOver20ProductsCount] = useState(0);
  const [between10And20ProductsCount, setBetween10And20ProductsCount] = useState(0);
  const [forceUpdateKey, setForceUpdateKey] = useState(0);
  const [productsToShow, setProductsToShow] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeliveryDateModal, setShowDeliveryDateModal] = useState(false);
  const [hasIncompatibleItems, setHasIncompatibleItems] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Fetch subscription details from the backend
  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      const userDetails = JSON.parse(localStorage.getItem('AficFreshuserDetails'));
      const userEmail = userDetails?.email;
      const token = localStorage.getItem('AficFreshtoken'); // Typically stored separately

  
      if (!userEmail) {
        setLoading(false);
        return;
      }
  
      try {
        const response = await axios.get(
          `https://afric-fresh-backend.onrender.com/user/subscription-details?email=${userEmail}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        const subscription = response.data.subscription;
  
        // Get the current month in 'YYYY-MM' format
        const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
  
        // Check if any subscription has an order for the current month
        const hasOrderThisMonth = subscription.some(sub => 
          sub.orders.some(order => order.orderMonth === currentMonth)
        );
  
        if (hasOrderThisMonth) {
          // Redirect to homepage if the user has already placed an order this month
          navigate('/');
        }
  
        // Check if the user is subscribed to any plan
        const activeSubscription = subscription.find(sub => sub.status === 'active');
        if (activeSubscription) {
          setSubscriptionStatus(activeSubscription.type);
        }
        
        setLoading(false);
      } catch (error) {
        console.log('Error fetching subscription details:', error);
        setError('Failed to fetch subscription details');
        setLoading(false);
      }
    };
  
    fetchSubscriptionDetails();
  }, [navigate]);

  // Redirect if the user is subscribed to a different box type
  useEffect(() => {
    if (subscriptionStatus && subscriptionStatus !== 'Large Box') {
      navigate(subscriptionStatus === 'Small Box' ? '/smallbox' : '/');
    }
  }, [subscriptionStatus, navigate]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        // Fetch products from backend based on subscription type

      const token = localStorage.getItem('AficFreshtoken'); // Typically stored separately
        const response = await axios.get(
          `https://afric-fresh-backend.onrender.com/user/subscrition/products?subscriptionType=Large box`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        
        if (response.data && Array.isArray(response.data)) {
          // Format the data to match the expected structure
          const formattedProducts = response.data.map(product => ({
            id: product._id || product.productId,
            name: product.name,
            brand: product.brand,
            price: product.price,
            img: product.img,
            description: product.description,
            category: product.category
          }));
          
          setProductsToShow(formattedProducts);
        } else {
          setError('Invalid product data received');
        }

        const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
        // Check for incompatible order types
        const hasIncompatible = storedCartItems.some(item => item.orderType !== "Large Box");
        setHasIncompatibleItems(hasIncompatible);

        setCartCount(storedCartItems.reduce((acc, item) => acc + item.quantity, 0));
        setUniqueProductsInCart(storedCartItems.length);
        setOver20ProductsCount(storedCartItems.filter((item) => parsePrice(item.price) > 20).length);
        setBetween10And20ProductsCount(
          storedCartItems.filter((item) => {
            const itemPrice = parsePrice(item.price);
            return itemPrice >= 10 && itemPrice <= 20;
          }).length
        );
        
        setLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setError('Failed to load products. Please try again later.');
        setLoading(false);
      }
    };

    fetchProducts();
  }, [forceUpdateKey]);

  const updateCartCount = () => {
    const storedCartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
    // Check for incompatible order types
    const hasIncompatible = storedCartItems.some(item => item.orderType !== "Large Box");
    setHasIncompatibleItems(hasIncompatible);

    setCartCount(storedCartItems.reduce((acc, item) => acc + item.quantity, 0));
    setUniqueProductsInCart(storedCartItems.length);
    setOver20ProductsCount(storedCartItems.filter((item) => parsePrice(item.price) > 20).length);
    setBetween10And20ProductsCount(
      storedCartItems.filter((item) => {
        const itemPrice = parsePrice(item.price);
        return itemPrice >= 10 && itemPrice <= 20;
      }).length
    );

    if (storedCartItems.length === 10) {
      setShowConfirmModal(true);
    }
    setForceUpdateKey((prevKey) => prevKey + 1);
  };

  const handleConfirmSelection = () => {
    setShowConfirmModal(false);
    setShowDeliveryDateModal(true);
  };

  const clearCart = () => {
    localStorage.removeItem('cartItems');
    setHasIncompatibleItems(false);
    updateCartCount();
  };

  // Loading and error states
  if (loading) {
    return (
      <>
        <Navbar cartCount={cartCount} updateCartCount={updateCartCount} />
        <div className="min-h-screen flex justify-center items-center p-8 lg:mt-[70px] mt-[130px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading products...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Navbar cartCount={cartCount} updateCartCount={updateCartCount} />
        <div className="min-h-screen flex justify-center items-center p-8 lg:mt-[70px] mt-[130px]">
          <div className="text-center bg-red-100 p-8 rounded-lg max-w-md">
            <p className="text-red-600 font-semibold text-lg">{error}</p>
            <button 
              onClick={() => setForceUpdateKey(prev => prev + 1)}
              className="mt-4 bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar cartCount={cartCount} updateCartCount={updateCartCount} />
      <div className="min-h-screen p-6 max-sm:p-3 lg:mt-[70px] mt-[130px] bg-gray-50">
        {/* Box Header */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h1 className="text-3xl font-bold text-green-800">Large Box Subscription</h1>
              <p className="text-gray-600 mt-2">Select any 10 products for $99/Month - Delivered to your doorstep</p>
            </div>
            
            <div className="mt-4 md:mt-0">
              <div className="bg-green-100 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <span className="font-semibold text-green-800">Selected: </span>
                  <span className="bg-green-700 text-white px-2 py-1 rounded-md">{uniqueProductsInCart}/10</span>
                </div>
                
                {uniqueProductsInCart > 0 && (
                  <div className="mt-2 text-sm text-green-700">
                    {over20ProductsCount > 0 && (
                      <div>Premium items ($20+): {over20ProductsCount}/1</div>
                    )}
                    {between10And20ProductsCount > 0 && (
                      <div>Standard items ($10-$20): {between10And20ProductsCount}/2</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Incompatible Cart Items Error */}
        {hasIncompatibleItems && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md shadow">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium">
                  Your cart contains items from a different box type. Please complete your current order or clear the cart before selecting items from the Large Box.
                </p>
                <div className="mt-2">
                  <button
                    onClick={clearCart}
                    className="bg-red-600 hover:bg-red-700 text-white py-1 px-3 rounded text-sm"
                  >
                    Clear Cart
                  </button>
                </div>
              </div>
              <button
                onClick={() => setHasIncompatibleItems(false)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Product Filters - could be expanded in the future */}
        <div className="mb-6 flex flex-wrap gap-2">
          {/* Could add category filters, search, etc. here */}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {productsToShow.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              updateCartCount={updateCartCount}
              forceUpdateKey={forceUpdateKey}
              uniqueProductsInCart={uniqueProductsInCart}
              over20ProductsCount={over20ProductsCount}
              between10And20ProductsCount={between10And20ProductsCount}
              hasIncompatibleItems={hasIncompatibleItems}
              onCartConflictError={() => setHasIncompatibleItems(true)}
            />
          ))}
        </div>
        
        {/* Checkout Section - Fixed at bottom when items in cart */}
        {uniqueProductsInCart > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t p-4 z-10">
            <div className="container mx-auto flex justify-between items-center">
              <div>
                <span className="text-lg font-semibold">Your Selection: {uniqueProductsInCart}/10 items</span>
                <p className="text-sm text-gray-600">Monthly subscription: $99</p>
              </div>
              <button
                onClick={() => setShowConfirmModal(true)}
                className={`bg-green-600 text-white py-2 px-6 rounded-lg hover:bg-green-700 transition duration-200 ${
                  uniqueProductsInCart < 10 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={uniqueProductsInCart < 10}
              >
                {uniqueProductsInCart < 10 ? `Add ${10 - uniqueProductsInCart} more item${10 - uniqueProductsInCart !== 1 ? 's' : ''}` : 'Confirm Selection'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Modal */}
      {showConfirmModal && (
        <ConfirmModal
          onClose={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmSelection}
        />
      )}

      {/* Delivery Date Modal */}
      {showDeliveryDateModal && (
        <DeliveryDateModal
          onClose={() => setShowDeliveryDateModal(false)}
        />
      )}

      <Footer />
    </>
  );
};

export default LargeBoxPage;